import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, Target, CheckCircle2, AlertTriangle, Loader2, X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../contexts/AuthContext';
import { PerfKpiCards } from '../../components/perf/PerfKpiCards';
import { CommissionCards } from '../../components/perf/CommissionCards';
import { LeaderboardTable } from '../../components/perf/LeaderboardTable';
import { CommercialDrawer } from '../../components/perf/CommercialDrawer';
import { TargetsDialog } from '../../components/perf/TargetsDialog';
import { CommercialInterimsCard } from '../../components/admin/CommercialInterimsCard';
import { CommercialCoverageCard } from '../../components/admin/CommercialCoverageCard';
import { currentMonth, lastMonths, monthLabel, fmtXof } from '../../components/perf/perfMeta';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommercialDashboardPage() {
  const { user } = useAuth();
  const canManage = ['admin', 'lead_commercial'].includes(user?.role);
  const isTeamView = ['admin', 'lead_commercial', 'finance'].includes(user?.role);

  const [period, setPeriod] = useState(currentMonth());
  const [data, setData] = useState(null);
  const [meData, setMeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null);
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [targetPreselect, setTargetPreselect] = useState(null);
  const [validating, setValidating] = useState(false);
  const [bannerClosed, setBannerClosed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isTeamView) {
        const { data: d } = await axios.get(`${API}/admin/commercials/dashboard`, { params: { period } });
        setData(d);
      } else {
        const { data: d } = await axios.get(`${API}/admin/commercials/${user.id}/dashboard`, { params: { period } });
        setMeData(d);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Impossible de charger la performance'); }
    finally { setLoading(false); }
  }, [period, isTeamView, user?.id]);

  useEffect(() => { load(); }, [load]);

  const validateAll = async () => {
    const toValidate = data?.totals?.commissions?.toValidate || 0;
    if (!toValidate) { toast.info('Aucune commission à valider'); return; }
    if (!window.confirm(`Valider (approuver) toutes les commissions en attente de ${monthLabel(period)} ?`)) return;
    setValidating(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/commissions/validate-batch`, null, { params: { period } });
      toast.success(`${r.approved} commission(s) approuvée(s)`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la validation'); }
    finally { setValidating(false); }
  };

  const exportCsv = () => {
    const rows = data?.leaderboard || (meData ? [{ commercialName: meData.commercialName || 'Moi', ...meData }] : []);
    if (rows.length === 0) { toast.info('Aucune donnée à exporter'); return; }
    const header = ['Commercial', 'Clients', 'CA_realise', 'Objectif', 'Progression_%', 'Ops_payees', 'Factures', 'Commission_pending', 'Commission_paid'];
    const csv = [header.join(',')].concat(rows.map((r) => [
      `"${(r.commercialName || '').replace(/"/g, '""')}"`, r.portfolioSize ?? 0, r.ca?.realised ?? 0, r.ca?.target ?? 0,
      Math.round(r.ca?.pct ?? 0), r.ops?.realised ?? 0, r.invoices?.realised ?? 0,
      (r.commissions?.pending ?? 0) + (r.commissions?.approved ?? 0), r.commissions?.paid ?? 0,
    ].join(','))).join('\n');
    const url = window.URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a'); a.href = url; a.download = `performance_${period}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    toast.success('Export CSV généré');
  };

  const openDefineTarget = (commercial = null) => { setTargetPreselect(commercial); setTargetsOpen(true); };

  const totals = isTeamView ? data?.totals : {
    ca: meData?.ca?.realised, ops: meData?.ops?.realised, caTarget: meData?.ca?.target,
    progression: meData?.ca?.pct, cashRecovery: meData?.cashRecovery,
    commissions: { toValidate: meData?.commissions?.pending, approved: meData?.commissions?.approved, paid: meData?.commissions?.paid, remaining: meData?.commissions?.remaining },
  };
  const commercials = data?.leaderboard || [];
  const noTarget = isTeamView && data && (data.totals?.commercialsWithTarget || 0) === 0;

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-5 gap-3">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div></div>;
  }

  return (
    <div className={`space-y-6 transition-all ${drawer ? 'xl:pr-[460px]' : ''}`} data-testid="perf-commerciale-page">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">Performance commerciale — {monthLabel(period)}</h1>
          <p className="mt-1 text-sm text-gray-500">Suivez le chiffre d'affaires, les opérations payées, les commissions et la performance des commerciaux.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="h-9 w-[150px] text-xs" data-testid="perf-period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{lastMonths(12).map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9 text-xs" onClick={exportCsv} data-testid="perf-export-btn"><Download className="mr-1.5 h-3.5 w-3.5" /> Exporter</Button>
          {canManage && <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => openDefineTarget(null)} data-testid="perf-define-targets-btn"><Target className="mr-1.5 h-3.5 w-3.5" /> Définir les objectifs</Button>}
          {canManage && (
            <Button size="sm" className="h-9 bg-emerald-600 text-xs hover:bg-emerald-700" onClick={validateAll} disabled={validating} data-testid="perf-validate-commissions-btn">
              {validating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />} Valider les commissions
            </Button>
          )}
        </div>
      </div>

      {/* Résultat du mois */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">Résultat du mois</h2>
        <PerfKpiCards totals={totals} />
      </div>

      {/* Commissions */}
      <div>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-400">Commissions</h2>
        <CommissionCards commissions={totals?.commissions} />
      </div>

      {/* Bandeau objectifs */}
      {canManage && noTarget && !bannerClosed && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3" data-testid="perf-no-target-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 text-blue-500" />
          <p className="flex-1 text-sm text-blue-900">Aucun objectif commercial n'est défini pour {monthLabel(period)}. Définissez des objectifs pour suivre la performance de l'équipe et calculer les commissions.</p>
          <Button size="sm" className="h-8 bg-blue-600 text-xs text-white hover:bg-blue-700" onClick={() => openDefineTarget(null)} data-testid="perf-banner-define-btn">Définir les objectifs</Button>
          <button type="button" onClick={() => setBannerClosed(true)} className="text-blue-400 hover:text-blue-600"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Classement (team) ou note perso */}
      {isTeamView ? (
        <LeaderboardTable rows={commercials} totals={data?.totals} onOpen={setDrawer} />
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-500 shadow-sm" data-testid="perf-personal-note">
          Voici votre performance personnelle pour {monthLabel(period)}. Le classement d'équipe est réservé aux responsables.
        </div>
      )}

      {/* Couverture commerciale (clients & opérations sans commercial) */}
      {isTeamView && <CommercialCoverageCard canManage={canManage} />}

      {/* Congés & intérims (réservé aux responsables) */}
      {canManage && <CommercialInterimsCard />}

      {/* Drawer détail */}
      {drawer && (
        <CommercialDrawer commercial={drawer} period={period} canManage={canManage}
          onClose={() => setDrawer(null)} onDefineTarget={(c) => openDefineTarget(c)} onChanged={load} />
      )}

      {/* Dialog objectifs */}
      <TargetsDialog open={targetsOpen} onClose={() => setTargetsOpen(false)} commercials={commercials} period={period} preselect={targetPreselect} onSaved={load} />
    </div>
  );
}
